import { SearchAssets } from "js/core/utilities/searchAssets";
import { uuid } from "js/core/utilities/utilities";
import {
    ASSET_FILETYPE,
    AssetType,
    AuthoringBlockType,
    BackgroundStyleType,
    ListStyleType,
    TextStyleType
} from "common/constants";
import { ds } from "js/core/models/dataService";
import { iconSearch } from "js/core/services/iconSearch";
import { getCanvasBundle } from "js/canvas";
import { appVersion } from "js/config";
import { _ } from "js/vendor";

const searchAssets = new SearchAssets();
const textPositions = ["center", "left", "top_right", "bottom_left", "center"];
const headlinePositions = ["left", "right"];
const textStyles = ["transparent_dark_box", "white_box"];
const backgroundStyles = [BackgroundStyleType.LIGHT, BackgroundStyleType.DARK, BackgroundStyleType.ACCENT];
const backgroundColors = ["background_dark", "background_light", "background_accent", "background_light"];
const accentColors = ["theme", "accent1", "accent2", "accent3"];
const allBackgroundColors = backgroundColors.concat(accentColors);
const headerPositions = ["top", "left", "top"];
const trayTypes = ["tray"];
const genericTopics = ["welcome", "introduction", "conclusion"];
const imageSearchSuggestions = ["skyline", "success", "inspiration", "landscape"];
const fallbackIcons = ["fog-bridge", "map-marker", "people-network"];
const fallbackImages = ["b942ed07e0e925dcf831a9521649e4aa69f855c2820c43832889d12d104f950b-I", "cbc5a774e0846c676315038d33d7218a3a454172d1bd2ddaa6fbce915121bd79-I", "b147f15fc3f272cc83207f360395e65cd5204e7264a449e25c6e325a8329a6f3-I", "bf83cfba1ffff04cc7b629dd5770efd7e3d0dbfad79c43993064596d16967dab-I"];
const chartTypes = ["line", "column", "area"];

function randomIntFromInterval(min, max) { // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
}

function getRandomItem(list) {
    return list[randomIntFromInterval(0, list.length - 1)];
}

async function getImagesForAiGeneration(keyword) {
    let response = await searchAssets.searchImages(keyword);
    return response.results;
}

async function convertImageToAsset(imageProps) {
    try {
        const url = await imageProps.service.getDownloadUrl(imageProps);
        const asset = await ds.assets.getOrCreateImage({
            url,
            fileType: ASSET_FILETYPE.JPEG,
            assetType: AssetType.IMAGE,
            name: imageProps.title,
            metadata: imageProps.metadata
        });
        return asset;
    } catch (err) {
        console.error("An error occurred when loading the image. Using fallback image.", imageProps, err);
        return {
            id: getRandomItem(fallbackImages)
        };
    }
}

async function chooseRandomIcon(text) {
    const { results } = await iconSearch(text);
    return getRandomItem(results)?.ref || getRandomItem(fallbackIcons);
}

function createLayout() {
    return {
        backgroundColor: getRandomItem(allBackgroundColors),
        backgroundStyle: "backgroundColor",
        slideColor: "neutral",
        headerPosition: getRandomItem(headerPositions),
        showFooter: "theme"
    };
}

function getBlock(html, textStyle = TextStyleType.BODY, listStyle) {
    return {
        id: uuid(),
        html,
        type: AuthoringBlockType.TEXT,
        textStyle,
        listStyle
    };
}

function parseTitle(title) {
    let result = "";
    let startTag = true;
    for (let i = 0; i < title.length; i++) {
        if (title[i] == "*") {
            if (startTag) {
                result += `<font class="emphasized">`;
                startTag = false;
            } else {
                result += "</font>";
                startTag = true;
            }
        } else {
            result += title[i];
        }
    }
    return result;
}

function createHeader(slide) {
    if (slide.title) {
        let header = {
            header: {
                text: {
                    blocks: [getBlock(parseTitle(slide.title), TextStyleType.HEADING)]
                }
            }
        };

        if (slide.subtitle || slide.description) {
            header.header.text.blocks.push(getBlock(slide.subtitle || slide.description, TextStyleType.BODY));
        }

        return header;
    }
}

function createAttribution(slide) {
    if (slide.attribution) {
        return {
            elementAttribution: {
                elementAttribution: {
                    blocks: [getBlock("*" + slide.attribution, TextStyleType.BODY)]
                }
            }
        };
    } else {
        return null;
    }
}

const TEMPLATE_VERSION = 9;

async function createTitleSlide(slide, images) {
    let image = getRandomItem(images);
    let asset = await convertImageToAsset(image);

    return {
        template_id: "title",
        version: 10,
        layout: createLayout(),
        states: [{
            primary: {
                text: {
                    blocks: [
                        {
                            id: uuid(),
                            html: parseTitle(slide.title),
                            type: "text",
                            textStyle: TextStyleType.HEADING,
                            indent: 0
                        }
                    ]
                },
                textStyle: "white_box",
                image: {
                    content_type: "image",
                    content_value: asset.id,
                    position: "background",
                }
            }
        }]
    };
}

async function createHeadlineSlide(slide, images) {
    const headlinePosition = getRandomItem(headlinePositions);
    const textStyle = getRandomItem(textStyles);
    const dividerSize = randomIntFromInterval(45, 70) / 100;

    let bodyBlocks = [];
    if (Array.isArray(slide.body)) {
        bodyBlocks = slide.body.map(body => (getBlock(body.body, TextStyleType.BODY)));
    } else {
        bodyBlocks = [getBlock(slide.body, TextStyleType.BODY)];
    }

    return {
        template_id: "headline2",
        version: 10,
        layout: {
            backgroundStyle: getRandomItem(backgroundStyles)
        },
        states: [{
            primary: {
                text: {
                    blocks: [
                        getBlock(parseTitle(slide.title), TextStyleType.HEADING),
                        ...bodyBlocks
                    ]
                },
                items: [
                    {
                        content_type: "image",
                        generateImage: {
                            search: slide.search,
                            pickRange: 4
                        },
                        cellColor: "auto",
                    }
                ],
                headlinePosition: headlinePosition,
                dividerSize: dividerSize
            }
        }]
    };
}

function createQuoteSlide(slide, images) {
    let quote = `“${slide.quote}”`;
    let blocks = [getBlock(parseTitle(quote), TextStyleType.HEADLINE)];
    if (slide.attribution.toLowerCase() != "anonymous") {
        blocks.push(getBlock(slide.attribution, TextStyleType.CAPTION));
    }

    return {
        template_id: "headline2",
        version: 10,
        layout: {
            backgroundColor: getRandomItem(allBackgroundColors),
            backgroundStyle: "backgroundColor",
        },

        states: [{
            primary: {
                text: {
                    blocks,
                    blockFontScales: {
                        caption: 1.5
                    }
                },
            }
        }]
    };
}

async function createChartSlide(slide) {
    if (slide.chartType == "pie") {
        return await createPieChartSlide(slide);
    }

    if (slide.data.length < 6 && (slide.yaxis.toLowerCase().contains("rating") || slide.yaxis.toLowerCase().contains("score") || slide.yaxis.toLowerCase().contains("rank"))) {
        return await createChartCompareSlide(slide);
    }

    const { slideTemplates: { chartUtils } } = await getCanvasBundle(appVersion);

    let chartType = (slide.chartType ?? "column").toLowerCase();
    if (!["line", "area", "column", "spline"].includes(chartType)) {
        chartType = "column";
    }

    let chart = chartUtils.getDefaultChartModel(chartType);
    chart.showXAxis = true;
    chart.showYAxis = true;
    chart.chartData.xAxis.axisTitle = true;
    chart.chartData.xAxis.axisTitleText = slide.xaxis;
    chart.chartData.xAxis.dateFormatting = "none";
    chart.chartData.yAxis.axisTitle = "edge";
    chart.chartData.yAxis.axisTitleText = slide.yaxis;
    chart.chartData.xAxis.categories = slide.data.map(d => d.x);
    chart.chartData.series = [{
        colorName: "chart1",
        id: "series1",
        marker: "none",
        name: "Data",
        type: chartType,
        data: slide.data.map(d => ({ y: parseFloat(d.y) }))
    }];

    return {
        template_id: `chart_${chartType}`,
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles),
            elementTextBlockPosition: "tray",
            showElementAttribution: slide.attribution != null
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items: [{
                    elementType: "chart",
                    chart: chart
                }]
            },
            elementTextBlock: {
                elementTextBlock: {
                    blocks: [{
                        id: uuid(),
                        type: AuthoringBlockType.TEXT,
                        textStyle: TextStyleType.TITLE,
                        html: slide.conclusion
                    }]
                }
            },
            ...createAttribution(slide)
        }]
    };
}

function createChartCompareSlide(slide) {
    let template;
    if (slide.data.length < 5) {
        template = getRandomItem(["compare_vertical_bars", "compare_radial_bars", "compare_horizontal_bars", "compare_bubbles"]);
    } else {
        template = getRandomItem(["compare_vertical_bars", "compare_horizontal_bars"]);
    }

    let maxValue = 100;
    if (slide.yaxis) {
        if (slide.yaxis.contains("1-100") || slide.yaxis.contains("1 - 100")) {
            maxValue = 100;
        } else if (slide.yaxis.contains("1-10") || slide.yaxis.contains("1 - 10")) {
            maxValue = 10;
        } else if (slide.yaxis.contains("1-5") || slide.yaxis.contains("1 - 5")) {
            maxValue = 5;
        } else {
            maxValue = _.max(slide.data.map(d => d.y));
        }
    }

    return {
        template_id: template,
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles),
            elementTextBlockPosition: "tray",
            showElementAttribution: slide.attribution != null,
            slideColor: "colorful"
        },
        states: [{
            ...createHeader(slide),
            primary: {
                scaling: "linear",
                format: "number",
                minValue: 0,
                maxValue: maxValue,
                items: slide.data.map(d => (
                    {
                        value: parseFloat(d.y),
                        text: {
                            blocks: [getBlock(d.x, TextStyleType.TITLE)]
                        }
                    }
                ))
            },
            elementTextBlock: {
                elementTextBlock: {
                    blocks: [{
                        id: uuid(),
                        type: AuthoringBlockType.TEXT,
                        textStyle: TextStyleType.TITLE,
                        html: slide.conclusion
                    }]
                }
            },
            ...createAttribution(slide)
        }]
    };
}

async function createPieChartSlide(slide) {
    slide.subtitle = slide.conclusion;

    return {
        template_id: "chart_pie",
        version: 10,
        layout: {
            headerPosition: "left",
            backgroundStyle: getRandomItem(backgroundStyles),
            // elementTextBlockPosition: "tray",
            showElementAttribution: slide.attribution != null
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items: [{
                    elementType: "piechart",
                    piechart: {
                        format: "percent",
                        isDonut: false,
                        chartScale: 0.9,
                        data: slide.data.map(d => ({ id: uuid(), color: "auto", value: d.y, label: { blocks: [getBlock(d.x, "_use_styles")] } }))
                    }
                }]
            },
            ...createAttribution(slide)
        }]
    };
}

async function createTextListSlide(slide) {
    let cols = 2;
    let itemsInCol = Math.ceil(slide.items.length / cols);

    let listStyle = slide.type === "ordered list" ? ListStyleType.NUMBERED : ListStyleType.BULLET;

    let col = {
        isMedia: false,
        text: {
            blocks: []
        }
    };
    let items = [col];
    for (let i = 0; i < slide.items.length; i++) {
        if (i >= itemsInCol && items.length < cols) {
            col = {
                isMedia: false,
                text: {
                    blocks: []
                }
            };
            items.push(col);
        }
        col.text.blocks.push(getBlock(slide.items[i].bullet || slide.items[i].title, TextStyleType.BULLET_LIST, listStyle));
        let details = getBlock(slide.items[i].details || slide.items[i].description, TextStyleType.BULLET_LIST, ListStyleType.TEXT);
        details.indent = 1;
        col.text.blocks.push(details);
    }

    return {
        template_id: "textlist",
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles),
            // elementTextBlockPosition: "tray",
            // trayLayout: "left_tray"
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items
            },
        }]
    };
}

async function createAgendaSlide(slide) {
    let cols = 2;
    let itemsInCol = Math.ceil(slide.items.length / cols);

    let listStyle = ListStyleType.NUMBERED;

    let col = {
        isMedia: false,
        text: {
            blocks: []
        }
    };
    let items = [col];
    for (let i = 0; i < slide.items.length; i++) {
        if (i >= itemsInCol && items.length < cols) {
            col = {
                isMedia: false,
                text: {
                    blocks: []
                }
            };
            items.push(col);
        }
        if (slide.items[i].type == "break") {
            col.text.blocks.push(getBlock(slide.items[i].title, TextStyleType.SECTION));
        } else {
            col.text.blocks.push(getBlock(slide.items[i].title, TextStyleType.BULLET_LIST, listStyle));
        }
    }

    return {
        template_id: "agenda",
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles),
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items
            },
        }]
    };
}

async function createIconListSlide(slide) {
    let items = [];
    for (let item of slide.items) {
        item.iconId = await chooseRandomIcon(item.icon);
    }

    return {
        template_id: "textgrid_vertical_icons",
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles),
            elementTextBlockPosition: "tray"
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items: slide.items.map(item => ({
                    text: {
                        blocks: [
                            getBlock(item.bullet, TextStyleType.TITLE),
                            getBlock(item.details, TextStyleType.BODY)
                        ]
                    },
                    content_type: "icon",
                    content_value: item.iconId,
                    frameType: "theme"
                }))
            },
            elementTextBlock: {
                color: getRandomItem(accentColors),
                elementTextBlock: {
                    blocks: [{
                        id: uuid(),
                        type: AuthoringBlockType.TEXT,
                        textStyle: TextStyleType.TITLE,
                        html: slide.conclusion
                    }]
                }
            }
        }]
    };
}

function createTimelineSlide(slide) {
    let connectors = [];
    let items = [];

    let x = 0.1;
    let xDelta = 0.8 / slide.items.length;

    for (let i = 0; i < slide.items.length; i++) {
        let y = (i % 2 == 0) ? 0.25 : 0.65;

        let item = {
            id: uuid(),
            nodeType: "bullet-text",
            text: {
                blocks: [{
                    id: uuid(),
                    type: AuthoringBlockType.TEXT,
                    textStyle: TextStyleType.TITLE,
                    html: slide.items[i].date
                }, {
                    id: uuid(),
                    type: AuthoringBlockType.TEXT,
                    textStyle: TextStyleType.BODY,
                    html: slide.items[i].text
                }]
            },
            textDirection: "right",
            hasBeenDragged: false,
            x, y
        };
        items.push(item);

        let connector = {
            id: "from-" + item.id,
            source: item.id,
            target: "/primary/timeline",
            endPointIsLocked: true,
            startAnchor: "anchor-left",
            endAnchor: "anchor-free",
            connectorType: "straight",
            canChangeConnectorType: false,
            startDecoration: "none",
            lineStyle: "solid",
            lineWeight: 2,
        };
        connectors.push(connector);

        x += xDelta;
    }

    return {
        template_id: "timeline",
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles),
            slideColor: "colorful"
        },
        states: [{
            ...createHeader(slide),
            primary: {
                annotations: {
                    connections: {
                        items: connectors
                    },
                    items: items
                }
            }
        }]
    };
}

function createVennSlide(slide) {
    return {
        template_id: "vennDiagram",
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles),
            slideColor: "colorful"
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items: slide.items.map((item, i) => ({
                    id: uuid(),
                    radius: 150,
                    color: "accent1",
                    text: {
                        blocks: [{
                            id: uuid(),
                            type: AuthoringBlockType.TEXT,
                            textStyle: TextStyleType.TITLE,
                            html: item.text
                        }]
                    }
                }))
            }
        }]
    };
}

async function createProcessDiagram(slide) {
    let template = "step_chevrons";

    return {
        template_id: template,
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles)
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items: slide.steps.map((item, i) => ({
                    id: uuid(),
                    text: {
                        blocks: [
                            getBlock(item.title, TextStyleType.TITLE),
                        ]
                    },
                    body: {
                        blocks: [
                            getBlock(item.description, TextStyleType.BODY),
                        ]
                    },
                    hilited: i == (slide.steps.length - 1)
                })),
                textLayout: getRandomItem(["below"])
            }
        }]
    };
}

async function createTeamSlide(slide) {
    let template = "team";
    if (slide.items.length > 4) {
        template = "textgrid_photos";
    }

    return {
        template_id: template,
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles)
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items: slide.items.map((item, i) => ({
                    id: uuid(),
                    color: "accent1",
                    content_type: "image",
                    generateImage: {
                        search: item.search,
                        pickRange: 1
                    },
                    text: {
                        blocks: [
                            getBlock(item.name, TextStyleType.TITLE),
                            getBlock(item.title || item.biography, TextStyleType.BODY)
                        ]
                    }
                }))
            }
        }]
    };
}

async function createPhotosWithTextSlide(slide) {
    return {
        template_id: "textgrid_photos",
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles)
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items: slide.items.map((item, i) => ({
                    id: uuid(),
                    color: "accent1",
                    content_type: "image",
                    generateImage: {
                        search: item.search,
                        pickRange: 1
                    },
                    text: {
                        blocks: [
                            getBlock(item.title, TextStyleType.TITLE),
                            getBlock(item.text, TextStyleType.BODY)
                        ]
                    }
                }))
            }
        }]
    };
}

function createTableSlide(slide, images) {
    let cols = slide.columns.map((col, index) => ({ index, style: "defaultRow", break: false, size: 50 }));
    let rows = slide.rows.map((row, index) => ({ index: index + 1, style: "defaultRow", break: false, size: 40 }));
    let colHeaderCells = slide.columns.map((col, index) => ({ col: index, row: 0, id: uuid(), format: "text", cellText: { text: col.toString() } }));
    let cells = slide.rows.map((row, rowIndex) => row.map((val, colIndex) => ({ col: colIndex, row: rowIndex + 1, id: uuid(), format: "text", cellText: { text: val.toString() } }))).flat();

    rows = [{ index: 0, style: "headerRow", break: false, size: 30 }, ...rows];

    for (let cell of cells) {
        if (cell.cellText.text.toLowerCase() == "yes") {
            cell.format = "icon";
            cell.content_type = "icon";
            cell.content_value = "check-yes";
        }
        if (cell.cellText.text.toLowerCase() == "no") {
            cell.format = "icon";
            cell.content_type = "icon";
            cell.content_value = "x";
        }
    }

    cells = cells.filter(cell => cell.col < cols.length && cell.row < rows.length);

    cells = [...colHeaderCells, ...cells];

    return {
        template_id: "table-v2",
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: getRandomItem(backgroundStyles),
            showElementAttribution: slide.attribution != null
        },
        states: [{
            ...createHeader(slide),
            primary: {
                showBorder: true,
                showColGridLines: true,
                showTopLeftCell: true,
                showRowGridLines: true,
                showHeaderCol: true,
                showHeaderRow: true,
                tableWidth: 1,
                tableHeight: Math.min(1, rows.length * .1),
                cols,
                rows,
                cells,
                aiGeneratedData: slide,
            },
            ...createAttribution(slide)
        }]
    };
}

function createWordCloudSlide(slide) {
    let wordList = [];
    let weight = 5;
    let count = 1;
    for (let word of slide.words) {
        wordList.push({
            word: word,
            weight: weight,
            layout: {}
        });
        count++;
        if (count > 5) {
            weight = Math.max(1, weight - 1);
            count = 1;
        }
    }

    return {
        template_id: "wordCloud",
        version: 10,
        layout: {
            headerPosition: "none",
            backgroundStyle: getRandomItem(backgroundStyles),
        },
        states: [{
            primary: {
                style: getRandomItem(["right_angles", "right_angles", "straight", "rotated"]),
                sort: getRandomItem(["size", "random"]),
                scale: 20,
                wordcloud: JSON.stringify(wordList)
            },
        }]
    };
}

async function createPhotoCollageSlide(slide) {
    let count = 4 + Math.round(Math.random() * 4);

    let items = [];
    for (let i = 0; i < count; i++) {
        items.push({
            content_type: "image",
            generateImage: {
                search: slide.search,
                assetType: AssetType.IMAGE,
                pickIndex: i
            }
        });
    }

    return {
        template_id: "collage",
        version: 10,
        layout: {
            headerPosition: "none",
        },
        states: [{
            primary: {
                aspectRatio: "fill",
                fullBleed: false,
                showGutter: true,
                layoutType: "grid",
                items
            }
        }]
    };
}

async function createLogoSlide(slide) {
    return {
        template_id: "logogrid",
        version: 10,
        layout: {
            headerPosition: "top",
            backgroundStyle: BackgroundStyleType.ACCENT,
            backgroundColor: "background_accent"
        },
        states: [{
            ...createHeader(slide),
            primary: {
                items: slide.companies.map(company => ({
                    content_type: "logo",
                    generateImage: {
                        search: company,
                        assetType: AssetType.LOGO,
                        pickRange: 1
                    }
                })),
                showFrame: true,
                backgroundColor: "white"
            }
        }]
    };
}

async function createBiographySlide(slide) {
    const headlinePosition = getRandomItem(headlinePositions);
    const dividerSize = 0.5;

    return {
        template_id: "headline2",
        version: 10,
        layout: {
            backgroundStyle: getRandomItem(backgroundStyles)
        },
        states: [{
            primary: {
                text: {
                    blocks: [
                        getBlock(slide.name, TextStyleType.HEADING),
                        getBlock(slide.biography, TextStyleType.BODY)
                    ]
                },
                items: [
                    {
                        content_type: "image",
                        generateImage: {
                            search: slide.search,
                            pickRange: 1
                        },
                        cellColor: "auto",
                    }
                ],
                headlinePosition: headlinePosition,
                dividerSize: dividerSize
            }
        }]
    };
}

export async function CreateItemForCollection(data) {
    let item = {
        text: {
            blocks: [getBlock(data.item, TextStyleType.TITLE)]
        }
    };

    if (data.search) {
        let images = await searchAssets.searchGoogleImages(data.search, "photo");
        if (images.length) {
            let asset = await searchAssets.createAssetFromGoogleImageDataUrl(data.item, images[0], AssetType.IMAGE);
            item.content_type = AssetType.IMAGE;
            item.content_value = asset.id;
        }
    }

    return item;
}

export async function GenerateSlideModel(slide, images) {
    let slideModel;

    switch (slide.type.toLowerCase()) {
        case "title":
            return await createTitleSlide(slide, images);
        case "text":
            return await createHeadlineSlide(slide, images);
        case "chart":
            return await createChartSlide(slide);
        case "list":
            if (slide.items.length < 5) {
                return await createIconListSlide(slide);
            } else {
                return await createTextListSlide(slide);
            }
        case "ordered list":
            return await createTextListSlide(slide);
        case "timeline":
            return await createTimelineSlide(slide);
        case "venn":
            return await createVennSlide(slide);
        case "team":
        case "people":
            return await createTeamSlide(slide);
        case "quote":
            return await createQuoteSlide(slide);
        case "table":
            return await createTableSlide(slide);
        case "wordcloud":
            return await createWordCloudSlide(slide);
        case "photos":
            return await createPhotoCollageSlide(slide);
        case "logos":
            return await createLogoSlide(slide);
        case "photo list":
            return await createPhotosWithTextSlide(slide);
        case "process":
            return await createProcessDiagram(slide);
        case "agenda":
            return await createAgendaSlide(slide);
        case "biography":
            if (slide.items) {
                // fix request when a biography slide returns an array
                for (let item of slide.items) {
                    item.title = item.name;
                    item.text = item.biography || item.text || item.title;
                }

                return await createPhotosWithTextSlide(slide);
            } else {
                return await createBiographySlide(slide);
            }
        default:
            console.error("Invalid slide type:" + slide.type);
            return await createHeadlineSlide(slide, images);
    }
}

export function FindImagesToGenerate(model) {
    let imagesToGenerate = [];
    let findAssetsToGenerate = obj => {
        for (let [key, value] of Object.entries(obj)) {
            if (key == "generateImage") {
                imagesToGenerate.push(obj);
            }
            if (typeof value == "object") {
                findAssetsToGenerate(value);
            }
        }
    };
    findAssetsToGenerate(model);

    return imagesToGenerate;
}

export async function GenerateImagesForItem(item, onComplete, onError) {
    let { pickRange = 1, search, assetType = AssetType.IMAGE } = item.generateImage;
    try {
        let images = await searchAssets.searchGoogleImages(search, assetType, Math.floor(Math.random() * pickRange));
        if (images.length) {
            let asset = await searchAssets.createAssetFromGoogleImageDataUrl(item.generateImage.search, images[0], assetType);
            item.content_value = asset.id;
            onComplete();
        }
    } catch (err) {
        console.error("Failed to generate image", err, search, pickRange, assetType);
        onError();
    }
}

